import {
  normalizeImportTable,
  parseImportCsv,
  type CurveImportMapping,
  type ImportCell,
  type ImportTable,
  type CropsterPdfCurve,
} from "@anthemreactv2/roast-domain";
import type { CellObject, WorkSheet } from "xlsx";
import { EVENT_NAMES, parseWorkspace, type RoastEvent, type Workspace } from "./workspace";

const MAX_FILE_BYTES = 20_000_000;
const MAX_TABLE_ROWS = 20_100;
const MAX_TABLE_COLUMNS = 100;
const MAX_CELL_CHARACTERS = 10_000;
const MAX_SHEETS = 20;
const MAX_WORKBOOK_CELLS = MAX_TABLE_ROWS * MAX_TABLE_COLUMNS;
const MAX_EXPANDED_WORKBOOK_BYTES = 64_000_000;
const MAX_ARCHIVE_FILES = 1_000;
const INVALID_CELL_PREFIX = "[Unsupported spreadsheet cell:";

export type RoastImportFile =
  | { kind: "workspace"; workspace: Workspace }
  | { kind: "pdf"; name: string; workspace: Workspace; report: CropsterPdfCurve }
  | { kind: "tables"; name: string; tables: ImportTable[] };

function spreadsheetCell(cell: CellObject | undefined, address: string): ImportCell {
  if (!cell) return null;
  // Keep unusable cells visible in the preview, including on unselected summary
  // sheets. Never trust a formula's cached value as a measured temperature.
  if (cell.f !== undefined || cell.F !== undefined) return `${INVALID_CELL_PREFIX} formula at ${address}]`;
  if (cell.t === "z" || cell.v === undefined || cell.v === null) return null;
  if (cell.t === "s") {
    if (typeof cell.v !== "string") return `${INVALID_CELL_PREFIX} text at ${address}]`;
    if (cell.v.length > MAX_CELL_CHARACTERS) throw new Error(`${address} exceeds the 10,000-character cell limit.`);
    return cell.v;
  }
  if (cell.t === "n" && typeof cell.v === "number" && Number.isFinite(cell.v)) return cell.v;
  const reason = cell.t === "b" ? "boolean" : cell.t === "e" ? "error" : "value";
  return `${INVALID_CELL_PREFIX} ${reason} at ${address}]`;
}

function hasSignature(bytes: Uint8Array, signature: number[]) {
  return signature.every((value, index) => bytes[index] === value);
}

/** Bound archive expansion before SheetJS allocates or parses worksheet content. */
function boundedXlsxArchive(bytes: Uint8Array): Uint8Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const damaged = () => new Error("The XLSX archive is damaged or inconsistent. Save a new Excel workbook and try again.");
  const zip64 = () => new Error("ZIP64 Excel archives are not supported. Save a smaller standard .xlsx workbook.");
  let end = bytes.length - 22;
  const earliest = Math.max(0, end - 65_535);
  while (end >= earliest && (view.getUint32(end, true) !== 0x06054b50 || end + 22 + view.getUint16(end + 20, true) !== bytes.length)) end--;
  if (end < earliest) throw damaged();
  const count = view.getUint16(end + 10, true);
  const directorySize = view.getUint32(end + 12, true);
  const directoryStart = view.getUint32(end + 16, true);
  if (count === 0xffff || directorySize === 0xffffffff || directoryStart === 0xffffffff ||
      (end >= 20 && view.getUint32(end - 20, true) === 0x07064b50)) throw zip64();
  if (view.getUint16(end + 4, true) || view.getUint16(end + 6, true) || view.getUint16(end + 8, true) !== count) {
    throw new Error("Split Excel archives are not supported. Save a single .xlsx workbook.");
  }
  if (count > MAX_ARCHIVE_FILES) throw new Error("The Excel archive contains too many files. Export a workbook containing only the curve sheets.");
  if (directoryStart + directorySize !== end) throw damaged();

  const checkExtraFields = (start: number, length: number) => {
    const stop = start + length;
    for (let offset = start; offset < stop;) {
      if (offset + 4 > stop) throw damaged();
      if (view.getUint16(offset, true) === 1) throw zip64();
      offset += 4 + view.getUint16(offset + 2, true);
      if (offset > stop) throw damaged();
    }
  };
  const localSizes: { offset: number; compressed: number; expanded: number }[] = [];
  let totalExpanded = 0;
  let cursor = directoryStart;
  for (let index = 0; index < count; index++) {
    if (cursor + 46 > end || view.getUint32(cursor, true) !== 0x02014b50) throw damaged();
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const compressed = view.getUint32(cursor + 20, true);
    const expanded = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const next = cursor + 46 + nameLength + extraLength + view.getUint16(cursor + 32, true);
    const local = view.getUint32(cursor + 42, true);
    if (compressed === 0xffffffff || expanded === 0xffffffff || local === 0xffffffff) throw zip64();
    if (flags & 0x2041) throw new Error("Encrypted Excel archives are not supported. Save an unencrypted workbook.");
    if (method !== 0 && method !== 8) throw new Error("This Excel archive uses unsupported compression. Save a standard .xlsx workbook.");
    totalExpanded += expanded;
    if (totalExpanded > MAX_EXPANDED_WORKBOOK_BYTES) throw new Error("The Excel workbook expands beyond the 64 MB limit. Export only the curve sheets.");
    if (next > end || view.getUint16(cursor + 34, true) || local + 30 > directoryStart ||
        view.getUint32(local, true) !== 0x04034b50) throw damaged();
    checkExtraFields(cursor + 46 + nameLength, extraLength);
    const localFlags = view.getUint16(local + 6, true);
    const localCompressed = view.getUint32(local + 18, true);
    const localExpanded = view.getUint32(local + 22, true);
    const localNameLength = view.getUint16(local + 26, true);
    const localExtraLength = view.getUint16(local + 28, true);
    const dataStart = local + 30 + localNameLength + localExtraLength;
    if (localCompressed === 0xffffffff || localExpanded === 0xffffffff) throw zip64();
    if (localFlags & 0x2041) throw new Error("Encrypted Excel archives are not supported. Save an unencrypted workbook.");
    if (localFlags !== flags || view.getUint16(local + 8, true) !== method || dataStart + compressed > directoryStart ||
        ((!(flags & 8) || localCompressed !== 0) && localCompressed !== compressed) ||
        ((!(flags & 8) || localExpanded !== 0) && localExpanded !== expanded) ||
        (method === 0 && compressed !== expanded) || (!compressed && expanded)) throw damaged();
    checkExtraFields(local + 30 + localNameLength, localExtraLength);
    // Zero is SheetJS's signal to grow the inflation buffer without a bound.
    // Permit only the two standard empty deflate streams when the size is zero.
    if (method === 8 && expanded === 0 &&
        !(compressed === 2 && bytes[dataStart] === 3 && bytes[dataStart + 1] === 0) &&
        !(compressed === 5 && bytes[dataStart] === 1 && bytes[dataStart + 1] === 0 && bytes[dataStart + 2] === 0 && bytes[dataStart + 3] === 255 && bytes[dataStart + 4] === 255)) {
      throw damaged();
    }
    localSizes.push({ offset: local, compressed, expanded });
    cursor = next;
  }
  if (cursor !== end) throw damaged();
  // SheetJS's inflater trusts local sizes. Data-descriptor ZIPs legitimately put
  // zero there, so copy the bounded central sizes to avoid unbounded allocation.
  // Strip the ZIP comment as well: SheetJS finds the last end-record signature.
  const bounded = bytes.slice(0, end + 22);
  const boundedView = new DataView(bounded.buffer, bounded.byteOffset, bounded.byteLength);
  boundedView.setUint16(end + 20, 0, true);
  for (const { offset, compressed, expanded } of localSizes) {
    boundedView.setUint32(offset + 18, compressed, true);
    boundedView.setUint32(offset + 22, expanded, true);
  }
  return bounded;
}

async function spreadsheetTables(bytes: Uint8Array, extension: "xls" | "xlsx"): Promise<ImportTable[]> {
  // .xls means binary BIFF (raw or CFB), not HTML/XML given an Excel extension.
  const compoundFile = hasSignature(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
  const legacyBiff = bytes[0] === 0x09 && [0x00, 0x02, 0x04, 0x08].includes(bytes[1]!);
  const zipFile = hasSignature(bytes, [0x50, 0x4b, 0x03, 0x04]);
  if (extension === "xlsx" ? !zipFile : !compoundFile && !legacyBiff) {
    throw new Error(`This is not a binary .${extension} workbook. Save it as an Excel workbook, or choose its original .csv file.`);
  }
  const input = extension === "xlsx" ? boundedXlsxArchive(bytes) : bytes;

  // Spreadsheet parsing is loaded only for Excel files; all content remains local.
  const XLSX = await import("xlsx");
  const names = XLSX.read(input, { type: "array", bookSheets: true }).SheetNames;
  if (!names?.length) throw new Error("The workbook has no worksheets.");
  if (names.length > MAX_SHEETS) throw new Error("Import a workbook with at most 20 worksheets.");
  const workbook = XLSX.read(input, {
    type: "array",
    cellFormula: true,
    cellDates: false,
    cellHTML: false,
    cellText: false,
    // Read one extra row so oversized files cannot be silently truncated.
    sheetRows: MAX_TABLE_ROWS + 1,
  });
  if ((extension === "xlsx" && workbook.bookType !== "xlsx") ||
      (extension === "xls" && workbook.bookType && !["xls", "biff2", "biff3", "biff4", "biff5", "biff8"].includes(workbook.bookType))) {
    throw new Error(`The file contents do not match .${extension}. Save an Excel .xls or .xlsx workbook.`);
  }

  let totalCells = 0;
  const tables: ImportTable[] = [];
  for (const name of workbook.SheetNames) {
    const sheet: WorkSheet | undefined = workbook.Sheets[name];
    if (!sheet || !sheet["!ref"]) continue;
    const range = XLSX.utils.decode_range(sheet["!fullref"] ?? sheet["!ref"]);
    const rowCount = range.e.r + 1;
    const columnCount = range.e.c + 1;
    if (rowCount > MAX_TABLE_ROWS || columnCount > MAX_TABLE_COLUMNS) {
      throw new Error(`Worksheet “${name}” exceeds 20,100 rows or 100 columns. Export only the curve readings.`);
    }
    totalCells += rowCount * columnCount;
    if (totalCells > MAX_WORKBOOK_CELLS) throw new Error("The workbook is too large to preview. Export fewer worksheets or columns.");
    const rows: ImportCell[][] = [];
    let hasContent = false;
    // Start at row/column zero to preserve original Excel row numbers and blanks.
    for (let r = 0; r < rowCount; r++) {
      const row: ImportCell[] = [];
      for (let c = 0; c < columnCount; c++) {
        const address = XLSX.utils.encode_cell({ r, c });
        const value = spreadsheetCell(sheet[address], `${name}!${address}`);
        if (value !== null && (typeof value === "number" || value.trim())) hasContent = true;
        row.push(value);
      }
      rows.push(row);
    }
    if (hasContent) tables.push({ name, rows });
  }
  if (!tables.length) throw new Error("The workbook contains no non-empty worksheets. Export a sheet with elapsed time and temperature readings.");
  return tables;
}

export async function readRoastImportFile(file: File, signal?: AbortSignal): Promise<RoastImportFile> {
  try {
    if (file.size > MAX_FILE_BYTES) throw new Error("Import files must be smaller than 20 MB.");
    const extension = file.name.toLowerCase().split(".").at(-1);
    if (extension === "pdf") {
      const { readRoastPdf } = await import("./pdf-file");
      const report = await readRoastPdf(new Uint8Array(await file.arrayBuffer()), signal);
      return { kind: "pdf", name: file.name, workspace: workspaceFromPdf(file.name, report), report };
    }
    if (extension === "json") return { kind: "workspace", workspace: parseWorkspace(JSON.parse(await file.text())) };
    if (extension === "csv") {
      return { kind: "tables", name: file.name, tables: [{ name: file.name, rows: parseImportCsv(await file.text()) }] };
    }
    if (extension === "xls" || extension === "xlsx") {
      return { kind: "tables", name: file.name, tables: await spreadsheetTables(new Uint8Array(await file.arrayBuffer()), extension) };
    }
    if (extension === "crc") throw new Error("This is a Cropster roast-curve transfer file (.crc), not a spreadsheet. Its binary format cannot be decoded here. In Cropster, export ‘Print full details’ with the time/temperature readings table and import that PDF. A CSV or Excel sheet with individual readings also works; changing column settings or renaming the .crc file will not convert it.");
    throw new Error("Choose a CSV, Excel .xls/.xlsx workbook, Cropster full-details PDF, or exported JSON workspace.");
  } catch (error) {
    throw new Error(`${file.name}: ${error instanceof Error ? error.message : "The file could not be read."}`);
  }
}

const eventNames = new Map<string, RoastEvent["name"]>(EVENT_NAMES.map(name => [name.toLowerCase(), name]));
for (const [name, aliases] of [
  ["Turning point", ["tp"]],
  ["Color change", ["colour change", "dry end", "drying end", "end of drying", "yellowing"]],
  ["First crack", ["fc", "fcs", "1c", "first crack start"]],
  ["Second crack", ["sc", "scs", "2c", "second crack start"]],
  ["Heat adjustment", ["gas", "gas adjustment", "heat change", "gas change"]],
  ["Airflow adjustment", ["airflow", "airflow change", "fan adjustment"]],
  ["Drop", ["roast end", "end of roast"]],
] as const) {
  for (const alias of aliases) eventNames.set(alias, name);
}

export function workspaceFromImport(name: string, rows: ImportCell[][], mapping: CurveImportMapping): Workspace {
  if (!name.trim()) throw new Error("The imported curve needs a name.");
  const normalized = normalizeImportTable(rows, mapping);
  const selected = [mapping.timeColumn, mapping.beanColumn, mapping.environmentColumn, mapping.eventColumn];
  for (let row = mapping.headerRow; row < rows.length; row++) {
    for (const column of selected) {
      if (column === null) continue;
      const value = rows[row]![column];
      if (typeof value === "string" && value.startsWith(INVALID_CELL_PREFIX)) {
        throw new Error(`Row ${row + 1}, column ${column + 1}: ${value} Export measured values as plain numbers and events as text.`);
      }
    }
  }
  const events: RoastEvent[] = normalized.events.map(event => ({
    id: crypto.randomUUID(),
    name: eventNames.get(event.text.toLowerCase().replace(/\s+/g, " ")) ?? "Note",
    elapsedMs: event.elapsedMs,
    // Even recognized markers retain the exact imported label in their note.
    note: event.text,
  }));
  return parseWorkspace({
    version: 2,
    // The existing portable "csv" provenance denotes a tabular file import,
    // including Excel. Keep stored workspace schemas compatible across versions.
    source: { name: name.trim().slice(0, 160), provenance: "csv", samples: normalized.samples },
    reference: null,
    events,
    history: [],
    playheadMs: 0,
  });
}

export function workspaceFromPdf(name: string, report: CropsterPdfCurve): Workspace {
  return parseWorkspace({
    version: 2,
    source: { name: name.trim().slice(0, 160), provenance: "csv", samples: report.samples },
    reference: null,
    events: report.events.map(event => ({
      id: crypto.randomUUID(), elapsedMs: event.elapsedMs,
      name: eventNames.get(event.text.toLowerCase().replace(/\s+/g, " ")) ?? "Note",
      note: event.note ? `${event.text}: ${event.note}` : event.text,
    })),
    history: [],
    // A printed report opens as a completed replay; no Drop event is inferred.
    playheadMs: report.samples.at(-1)!.elapsedMs,
  });
}
